import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_V2" as const;
export const CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const CashHandoverNumberSchema = Type.String({
  pattern: "^JB-[0-9]{8}-[0-9A-F]{12}$",
});
export type CashHandoverNumber = `JB-${string}-${string}`;

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;
const SOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveCashHandoverNumber(
  businessDate: string,
  shiftId: string,
): CashHandoverNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!SOURCE_ID_PATTERN.test(shiftId)) {
    throw new TypeError("shiftId must contain at least 12 safe characters");
  }
  return `JB-${businessDate.replaceAll("-", "")}-${fortyEightBitHex(
    `JB\0${businessDate}\0${shiftId}`,
  )}`;
}

const StableIdSchema = Type.String({
  minLength: 12,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$",
});
const ActorIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const NonNegativeFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SignedFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const DisplayNameSchema = Type.String({ minLength: 1, maxLength: 120 });
const NoteSchema = Type.String({ maxLength: 500 });

export const CashDifferenceApprovalAuditSchema = Type.Union([
  Type.Object(
    {
      status: Type.Literal("NOT_AVAILABLE"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Literal("NOT_REQUIRED"),
      closedAt: Type.String({ minLength: 1, maxLength: 64 }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Literal("SUCCEEDED"),
      approvalId: StableIdSchema,
      decidedByDisplayName: DisplayNameSchema,
      decidedAt: Type.String({ minLength: 1, maxLength: 64 }),
      decisionReason: Type.String({ minLength: 2, maxLength: 500 }),
      completedAt: Type.String({ minLength: 1, maxLength: 64 }),
      closedAt: Type.String({ minLength: 1, maxLength: 64 }),
    },
    { additionalProperties: false },
  ),
]);
export type CashDifferenceApprovalAudit = Static<typeof CashDifferenceApprovalAuditSchema>;

const PublicItemProperties = {
  handoverNumber: CashHandoverNumberSchema,
  businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  openedByDisplayName: DisplayNameSchema,
  handoverSubmittedByDisplayName: DisplayNameSchema,
  receivedByDisplayName: DisplayNameSchema,
  startedAt: Type.String({ minLength: 1, maxLength: 64 }),
  handoverSubmittedAt: Type.String({ minLength: 1, maxLength: 64 }),
  receivedAt: Type.String({ minLength: 1, maxLength: 64 }),
  openingCashFen: NonNegativeFenSchema,
  cashReceiptsFen: NonNegativeFenSchema,
  cashRefundsFen: NonNegativeFenSchema,
  cashInFen: NonNegativeFenSchema,
  cashOutFen: NonNegativeFenSchema,
  expectedCashFen: SignedFenSchema,
  countedCashFen: NonNegativeFenSchema,
  cashDifferenceFen: SignedFenSchema,
  startNote: NoteSchema,
  exceptionNote: NoteSchema,
  receiveNote: NoteSchema,
  approvalAudit: Type.Optional(CashDifferenceApprovalAuditSchema),
} as const;

export const CashDifferenceBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    shiftId: StableIdSchema,
    openedBy: ActorIdSchema,
    handoverSubmittedBy: ActorIdSchema,
    receivedBy: ActorIdSchema,
    ...PublicItemProperties,
  },
  { additionalProperties: false },
);
export type CashDifferenceBusinessMetricDetailSourceItem = Static<
  typeof CashDifferenceBusinessMetricDetailSourceItemSchema
>;

export const CashDifferenceBusinessMetricDetailItemSchema = Type.Object(PublicItemProperties, {
  additionalProperties: false,
});
export type CashDifferenceBusinessMetricDetailItem = Static<
  typeof CashDifferenceBusinessMetricDetailItemSchema
>;

const CountSchema = Type.Integer({
  minimum: 0,
  maximum: CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CAPACITY,
});
const FactCountSchema = Type.Integer({
  minimum: 1,
  maximum: CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CAPACITY,
});
const EnvelopeProperties = {
  contractVersion: Type.Literal(CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$" }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("CLOSED_SHIFT_OPENING_CASH_RECEIPTS_REFUNDS_AND_CASH_MOVEMENTS"),
  valueFen: SignedFenSchema,
  factCount: FactCountSchema,
  shortageFactCount: CountSchema,
  excessFactCount: CountSchema,
  balancedFactCount: CountSchema,
} as const;

export const CashDifferenceBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(CashDifferenceBusinessMetricDetailSourceItemSchema, {
      minItems: 1,
      maxItems: CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type CashDifferenceBusinessMetricDetailSnapshot = Static<
  typeof CashDifferenceBusinessMetricDetailSnapshotSchema
>;

export const CashDifferenceBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(CashDifferenceBusinessMetricDetailItemSchema, {
      minItems: 1,
      maxItems: CASH_DIFFERENCE_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type CashDifferenceBusinessMetricDetailReadModel = Static<
  typeof CashDifferenceBusinessMetricDetailReadModelSchema
>;

type DetailEnvelope = Pick<
  CashDifferenceBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "timeZone"
  | "generatedAt"
  | "valueFen"
  | "factCount"
  | "shortageFactCount"
  | "excessFactCount"
  | "balancedFactCount"
>;

function envelopeIsConsistent(value: DetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    value.factCount !== value.shortageFactCount + value.excessFactCount + value.balancedFactCount
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function publicItemIsConsistent(
  item: CashDifferenceBusinessMetricDetailItem,
  envelope: DetailEnvelope,
): boolean {
  if (
    item.businessDate !== envelope.businessDate ||
    item.handoverNumber.slice(3, 11) !== envelope.businessDate.replaceAll("-", "") ||
    !isCanonicalTimestamp(item.startedAt) ||
    !isCanonicalTimestamp(item.handoverSubmittedAt) ||
    !isCanonicalTimestamp(item.receivedAt) ||
    Date.parse(item.startedAt) > Date.parse(item.handoverSubmittedAt) ||
    Date.parse(item.handoverSubmittedAt) > Date.parse(item.receivedAt) ||
    Date.parse(item.receivedAt) > Date.parse(envelope.generatedAt) ||
    item.expectedCashFen !==
      item.openingCashFen +
        item.cashReceiptsFen +
        item.cashInFen -
        item.cashRefundsFen -
        item.cashOutFen ||
    !Number.isSafeInteger(item.expectedCashFen) ||
    item.cashDifferenceFen !== item.countedCashFen - item.expectedCashFen ||
    !Number.isSafeInteger(item.cashDifferenceFen)
  ) {
    return false;
  }
  if (!approvalAuditIsConsistent(item.approvalAudit, item.receivedAt, envelope.generatedAt)) {
    return false;
  }
  return [
    item.openedByDisplayName,
    item.handoverSubmittedByDisplayName,
    item.receivedByDisplayName,
    item.startNote,
    item.exceptionNote,
    item.receiveNote,
  ].every((value) => value === value.trim());
}

function approvalAuditIsConsistent(
  audit: CashDifferenceApprovalAudit | undefined,
  receivedAt: string,
  generatedAt: string,
): boolean {
  if (audit === undefined || audit.status === "NOT_AVAILABLE") return true;
  if (!isCanonicalTimestamp(audit.closedAt)) return false;
  const receivedMilliseconds = Date.parse(receivedAt);
  const closedMilliseconds = Date.parse(audit.closedAt);
  if (closedMilliseconds < receivedMilliseconds || closedMilliseconds > Date.parse(generatedAt)) {
    return false;
  }
  if (audit.status === "NOT_REQUIRED") return true;
  if (
    !isCanonicalTimestamp(audit.decidedAt) ||
    !isCanonicalTimestamp(audit.completedAt) ||
    audit.decidedByDisplayName !== audit.decidedByDisplayName.trim() ||
    audit.decisionReason !== audit.decisionReason.trim()
  ) {
    return false;
  }
  const decidedMilliseconds = Date.parse(audit.decidedAt);
  const completedMilliseconds = Date.parse(audit.completedAt);
  return (
    completedMilliseconds >= decidedMilliseconds &&
    completedMilliseconds >= receivedMilliseconds &&
    closedMilliseconds >= completedMilliseconds
  );
}

function itemsAreConsistent(
  items: readonly CashDifferenceBusinessMetricDetailItem[],
  envelope: DetailEnvelope,
): boolean {
  if (items.length !== envelope.factCount) return false;
  const numbers = new Set<string>();
  let valueFen = 0;
  let shortageFactCount = 0;
  let excessFactCount = 0;
  let balancedFactCount = 0;
  let previousReceivedAt = Number.POSITIVE_INFINITY;
  let previousNumber = "";
  for (const item of items) {
    const receivedAt = Date.parse(item.receivedAt);
    if (
      numbers.has(item.handoverNumber) ||
      !publicItemIsConsistent(item, envelope) ||
      receivedAt > previousReceivedAt ||
      (receivedAt === previousReceivedAt &&
        previousNumber !== "" &&
        item.handoverNumber <= previousNumber)
    ) {
      return false;
    }
    valueFen += item.cashDifferenceFen;
    if (!Number.isSafeInteger(valueFen)) return false;
    if (item.cashDifferenceFen < 0) shortageFactCount += 1;
    else if (item.cashDifferenceFen > 0) excessFactCount += 1;
    else balancedFactCount += 1;
    numbers.add(item.handoverNumber);
    previousReceivedAt = receivedAt;
    previousNumber = item.handoverNumber;
  }
  return (
    valueFen === envelope.valueFen &&
    shortageFactCount === envelope.shortageFactCount &&
    excessFactCount === envelope.excessFactCount &&
    balancedFactCount === envelope.balancedFactCount
  );
}

function sourceItemIsConsistent(
  item: CashDifferenceBusinessMetricDetailSourceItem,
  envelope: DetailEnvelope,
): boolean {
  try {
    return (
      publicItemIsConsistent(item, envelope) &&
      item.handoverNumber === deriveCashHandoverNumber(item.businessDate, item.shiftId)
    );
  } catch {
    return false;
  }
}

export function isCashDifferenceBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is CashDifferenceBusinessMetricDetailSnapshot {
  if (
    !Value.Check(CashDifferenceBusinessMetricDetailSnapshotSchema, value) ||
    !envelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  const shiftIds = new Set<string>();
  for (const item of value.items) {
    if (shiftIds.has(item.shiftId) || !sourceItemIsConsistent(item, value)) return false;
    shiftIds.add(item.shiftId);
  }
  return itemsAreConsistent(value.items, value);
}

export function isCashDifferenceBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is CashDifferenceBusinessMetricDetailReadModel {
  return (
    Value.Check(CashDifferenceBusinessMetricDetailReadModelSchema, value) &&
    envelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate)) &&
    itemsAreConsistent(value.items, value)
  );
}
